import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { FaStarOfLife } from 'react-icons/fa';
import Loader from '../components/Loader';
import { getTestimonialAPI, updateTestimonialAPI} from '../../services/api/Testimonial';

const EditTestimonial = () => {
    const { id } = useParams();
    const user = useSelector((state) => state.LoginReducer.authData);
    const token = user.data.token;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string()
            .min(3, 'Name must be at least 3 characters long')
            .required('Name is required'),
        comment: Yup.string()
        .max(255, 'Comment must be less than or equal to 255 characters')
            .required('Comment is required'),
        image: Yup.mixed()
        .nullable()
        .test('fileType', 'Only JPEG and PNG files are allowed', (value) => {
            if (value) {
                return value && (value.type === 'image/jpeg' || value.type === 'image/png');
            }
          return true;
        })
        .test('fileSize', 'File size exceeds the 2MB limit', (value) => {
          if (value) {
            return value.size <= 2 * 1024 * 1024;
          }
          return true;
        }),
        
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            comment: '',
            image: null,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('comment', values.comment);
            formData.append('image', values.image);
            await updateTestimonial(formData);
        },
    });

    useEffect(() => {
        const fetchTestimonial = async () => {
            try {
                const data = await getTestimonialAPI(id, token);
                if (data === null) {
                    navigate('/404');
                } else {
                    formik.setValues({
                        name: data.name,
                        comment: data.comment,
                        image: null,
                        imagePreview: data.image,
                    });
                }                  
                
            } catch (error) {
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not fetch testimonial data.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        };

        fetchTestimonial();
    }, [id]);

    // Handle image change
    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        formik.setFieldValue('image', file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue('imagePreview', reader.result);  // Add a preview state if necessary
            };
            reader.readAsDataURL(file);
        }
    };

    const updateTestimonial = async (formData) => {
        try {
            const data = await updateTestimonialAPI(formData, id, token);
            setLoading(false);
            if (data.status) {
                Swal.fire({
                    title: 'Testimonial Updated!',
                    text: 'The testimonial has been Updated successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    navigate('/testimonials');
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            ['link', 'image'],
            [{ 'align': [] }],
            ['clean']
        ],
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Testimonial">
            <div className="container table-container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="name">Testimonial Name<FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" name="name" id="name" placeholder="Enter Testimonial Name" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name} />
                                </div>
                                {formik.touched.name && formik.errors.name ? ( <label className="text-danger">{formik.errors.name}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="comment">Comment <FaStarOfLife className='text-danger' /></label>
                                    <textarea className="form-control mt-1" rows={3} name="comment" id="comment" placeholder='comment' onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.comment}>{formik.values.comment}</textarea>
                                </div>
                                {formik.touched.comment && formik.errors.comment ? ( <label className="text-danger">{formik.errors.comment}</label>) : null}
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="image">User Image</label>
                                    <input type="file" className="form-control mt-1" name="image" id="image" accept="image/*" onChange={handleImageChange} />
                                </div>
                                {formik.touched.image && formik.errors.image ? ( <label className="text-danger">{formik.errors.image}</label>) : null}
                            </div>
                            {formik.values.imagePreview && (
                                <div className="col-md-6">
                                    <img className='image-preview' src={formik.values.imagePreview} alt="Image Preview" />
                                </div>
                            )}
                        </div>
                        <div className="row mt-3">
                            <div className="col-md-6 col-12 d-flex gap-3">
                                <Link to={'/categories'} type="submit" className="btn btn-dark cancel_btn">Cancel</Link>
                                <button type="submit" className="btn btn-primary submit_btn">Submit</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    )
}

export default EditTestimonial;